import { inject } from "@angular/core";
import { CanActivateFn, Router, Routes, UrlTree } from "@angular/router";
import { MsalGuard } from "@azure/msal-angular";

import { AccountPendingComponent } from "./account-pending.component";
import { AccountPendingGuard } from "./account-pending.guard";
import { InvalidCarrierComponent } from "./carrier/invalid-carrier.component";
import { InvalidCarrierGuard } from "./carrier/invalid-carrier.guard";
import { UserService } from "./core/auth";
import { LoadingComponent } from "./core/elements/loading.component";
import { HasPartnersGuard } from "./has-partners.guard";
import { LoginComponent } from "./login.component";
import { RedirectGuard } from "./redirect.guard";
import { RegistrationFormComponent } from "./registration-form.component";
import {
  NonVendorRedirectGuard,
  VendorPortalComponent,
} from "./vendor-portal.component";
// Other route components and guards...

const defaultRouteGuard: CanActivateFn = async (): Promise<UrlTree> => {
  const router = inject(Router);
  const user = inject(UserService);

  await user.isLoaded;

  return user.isCarrier()
    ? router.parseUrl("/start")
    : router.parseUrl("/partners/_/sites/_");
};

export const routes: Routes = [
  // This route is needed as the return route for the MSAL login flow and must
  // remain an "unprotected route" (no `MsalGuard`).
  { path: "login", component: LoginComponent },
  // **Note regarding route guards:**
  //
  // `MsalGuard` currently seems to have a bug where it won't work as a
  // `canLoad` guard in our current scenario, so we have to use `canActivate`
  // even when loading a separate module.
  //
  // Likewise, in routes using `MsalGuard` for all other guards that wait for
  // the user to be loaded, we have to use `canActivate` or else they will lock
  // up waiting for the load, apparently preventing MSAL from redirecting to the
  // login page.
  //
  // See: https://github.com/angular/angular/issues/27826#issuecomment-451304062
  //
  // If `MsalGuard` is ever fixed to support `canLoad` properly, the other
  // guards can also likely be switched to `canLoad` where appropriate.
  {
    path: "",
    pathMatch: "full",
    canActivate: [MsalGuard, defaultRouteGuard],
    component: LoadingComponent,
  },
  {
    path: "partners",
    canActivate: [MsalGuard, HasPartnersGuard, InvalidCarrierGuard],
    loadChildren: () =>
      import("./partner/partner-app.routes").then((m) => m.routes),
  },

  {
    path: "vendor-portal",
    canActivate: [MsalGuard, NonVendorRedirectGuard],
    component: VendorPortalComponent,
  },
  {
    path: "redirect",
    canActivate: [MsalGuard, RedirectGuard],
    // Arbitrary component. It shouldn't ever be rendered as the
    // RedirectGuard should always redirect away regardless.
    component: LoadingComponent,
  },
  {
    path: "start",
    canActivate: [MsalGuard, InvalidCarrierGuard],
    loadChildren: () =>
      import("./carrier/view-select/carrier-view-select.routes").then(
        (m) => m.routes,
      ),
  },
  {
    path: "carrier",
    canActivate: [MsalGuard, InvalidCarrierGuard],
    loadChildren: () =>
      import("./carrier/carrier-app.routes").then((m) => m.routes),
  },
  {
    path: "account-pending",
    canActivate: [MsalGuard, AccountPendingGuard],
    component: AccountPendingComponent,
  },
  {
    path: "invalid-carrier",
    canActivate: [MsalGuard, InvalidCarrierGuard],
    component: InvalidCarrierComponent,
  },
  {
    path: "register",
    canActivate: [MsalGuard],
    component: RegistrationFormComponent,
  },
  // Redirects for old paths. Prefer not to use these going forward.
  { path: "admin/sites/new", redirectTo: "partners/_/sites/new" },
  { path: "admin/sites/:siteId", redirectTo: "partners/_/sites/:siteId/edit" },
  { path: "admin/sites", redirectTo: "partners/_/sites" },
  { path: "appointments", redirectTo: "partners/_/sites/_/appointments" },
  { path: "carriers", redirectTo: "partners/_/carriers" },
  { path: "dashboard", redirectTo: "partners/_/sites/_/dashboard" },
  { path: "global", redirectTo: "partners/_/sites/_/global" },
  { path: "help-assist", redirectTo: "partners/_/sites/_/help-assist" },
  { path: "purchase-orders", redirectTo: "partners/_/sites/_/purchase-orders" },
  { path: "schedule", redirectTo: "partners/_/sites/_/schedule" },
  { path: "settings", redirectTo: "partners/_/sites/_/settings" },
  { path: "site-setup", redirectTo: "partners/_/first-site" },
  { path: "vendors", redirectTo: "partners/_/sites/_/vendors" },
  // Send everything else through the default route guard.
  { path: "**", redirectTo: "" },
];
